import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { expertise } from "../mock";
import { CircleDot } from "lucide-react";

function TimelineItem({ e, i }) {
  const isLeft = i % 2 === 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative grid lg:grid-cols-2 gap-6 lg:gap-12 items-center"
    >
      <div className="absolute left-1/2 -translate-x-1/2 hidden lg:flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-[#73100D] z-10 brand-glow">
        <CircleDot className="w-4 h-4 text-[#73100D]" />
      </div>

      <motion.div
        initial={{ opacity: 0, x: isLeft ? -32 : 32 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className={`rounded-2xl border border-[#E5E5E5] bg-white p-6 lg:p-7 hover:border-[#73100D]/30 hover:shadow-[0_18px_50px_-18px_rgba(115,16,13,0.18)] transition-all duration-500 ${isLeft ? "lg:col-start-1 lg:text-right" : "lg:col-start-2"}`}
      >
        <div className={`flex items-center gap-3 ${isLeft ? "lg:justify-end" : ""}`}>
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#73100D] font-semibold">{e.year}</span>
          <span className="px-2.5 py-0.5 rounded-full bg-[#73100D]/10 text-[#73100D] text-xs font-semibold">{e.name}</span>
        </div>
        <h3 className="mt-3 text-xl md:text-2xl font-semibold text-[#111]">{e.title}</h3>
        <p className="mt-2 text-sm text-[#555] leading-relaxed">{e.desc}</p>
      </motion.div>

      <div className={isLeft ? "hidden lg:block" : "hidden lg:block lg:col-start-1 lg:row-start-1"} />
    </motion.div>
  );
}

export default function Expertise() {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 30%"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="expertise" ref={ref} className="relative py-14 lg:py-20">
      <div className="absolute inset-0 section-tint pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }} className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
            Our Journey
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#111] leading-tight">
            A decade of{" "}
            <span className="brand-gradient-text">technology evolution</span>
          </h2>
          <p className="mt-4 text-[#555]">From custom web platforms to autonomous AI agents — our timeline of capability.</p>
        </motion.div>

        <div className="relative mt-16">
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[#E5E5E5] hidden lg:block" />
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-1/2 -translate-x-1/2 top-0 w-px bg-gradient-to-b from-[#5E0000] via-[#73100D] to-[#98413F] hidden lg:block"
          />

          <div className="space-y-14 lg:space-y-20">
            {expertise.map((e, i) => <TimelineItem key={e.name} e={e} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
